import { useEffect, useState, type MouseEvent } from 'react'
import { ArrowDownTrayIcon, DocumentIcon } from '@heroicons/react/24/outline'
import {
  MaterialTreeNode,
  SampleProjectApi,
} from '../../services/sampleProjectApi'

const TEXT_TYPES = new Set([
  'md', 'py', 'json', 'txt', 'yaml', 'yml', 'ts', 'js', 'tsx', 'jsx',
  'sh', 'bat', 'ps1', 'xml', 'html', 'htm', 'css', 'toml', 'ini', 'csv', 'env', 'example',
  'sql', 'gitignore', 'dockerignore', 'editorconfig', 'npmrc', 'prettierrc', 'eslintrc',
])

interface MaterialFolderFileViewerProps {
  courseId: number
  materialId: number
  node: MaterialTreeNode | null
  admin?: boolean
}

const MaterialFolderFileViewer = ({
  courseId,
  materialId,
  node,
  admin = false,
}: MaterialFolderFileViewerProps) => {
  const [content, setContent] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)

  const fileType = (node?.fileType || '').toLowerCase()
  const isText = TEXT_TYPES.has(fileType)
  const isPdf = fileType === 'pdf'
  const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(fileType)

  useEffect(() => {
    setContent(null)
    setError(null)
    setBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })

    if (!node) return

    let cancelled = false
    const load = async () => {
      try {
        setLoading(true)
        if (isText) {
          const data = await SampleProjectApi.getFileContent(
            courseId,
            materialId,
            node.id,
            admin
          )
          if (!cancelled) setContent(data.content)
        } else if (isPdf || isImage) {
          const url = SampleProjectApi.fileContentUrl(courseId, materialId, node.id, {
            raw: true,
            admin,
          })
          const token = localStorage.getItem('accessToken')
          const res = await fetch(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          })
          if (!res.ok) throw new Error('Không tải được file')
          const blob = await res.blob()
          if (!cancelled) setBlobUrl(URL.createObjectURL(blob))
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Không đọc được file')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [node?.id, courseId, materialId, admin, isText, isPdf, isImage])

  if (!node) {
    return (
      <div className="flex h-full min-h-[240px] flex-col items-center justify-center rounded-lg border border-dashed border-m2-outline-variant bg-m2-surface-low text-m2-on-surface-variant">
        <DocumentIcon className="mb-3 h-12 w-12 text-m2-outline" />
        <p>Chọn một file để xem nội dung</p>
      </div>
    )
  }

  const streamUrl = SampleProjectApi.fileContentUrl(courseId, materialId, node.id, {
    raw: true,
    admin,
  })

  const handleDownload = async (e: MouseEvent) => {
    e.preventDefault()
    try {
      const token = localStorage.getItem('accessToken')
      const res = await fetch(streamUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) throw new Error('Download failed')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = node.name
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      window.open(streamUrl, '_blank')
    }
  }

  const downloadBtn = (
    <button
      type="button"
      onClick={handleDownload}
      className="inline-flex items-center gap-1 text-sm text-m2-primary hover:underline"
    >
      <ArrowDownTrayIcon className="h-4 w-4" />
      Tải xuống
    </button>
  )

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-m2-outline-variant bg-m2-surface-container">
      <div className="flex items-center justify-between gap-3 border-b border-m2-outline-variant bg-m2-surface-low px-4 py-3">
        <div className="min-w-0">
          <h3 className="truncate font-semibold text-m2-on-surface">{node.name}</h3>
          <p className="truncate text-xs text-m2-on-surface-variant">{node.relativePath}</p>
        </div>
        {downloadBtn}
      </div>

      <div className="flex-1 overflow-auto p-4">
        {loading && <p className="text-sm text-m2-on-surface-variant">Đang tải nội dung...</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}

        {!loading && !error && isText && content !== null && (
          <pre className="overflow-x-auto rounded-md border border-m2-outline-variant bg-m2-surface-low p-4 text-sm leading-relaxed text-m2-on-surface">
            <code>{content}</code>
          </pre>
        )}

        {!loading && !error && isPdf && blobUrl && (
          <iframe title={node.name} src={blobUrl} className="h-[70vh] w-full rounded-md border border-m2-outline-variant" />
        )}

        {!loading && !error && isImage && blobUrl && (
          <img src={blobUrl} alt={node.name} className="max-h-[70vh] max-w-full rounded-md object-contain" />
        )}

        {!loading && !error && !isText && !isPdf && !isImage && (
          <div className="flex flex-col items-center justify-center py-16 text-m2-on-surface-variant">
            <DocumentIcon className="mb-3 h-12 w-12" />
            <p className="mb-4 text-sm">Loại file này không hỗ trợ xem trực tiếp</p>
            {downloadBtn}
          </div>
        )}
      </div>
    </div>
  )
}

export default MaterialFolderFileViewer
